import React, { useState, useEffect } from 'react';
import Visualizer from './components/Visualizer';
import Editor from './components/Editor';
import { DEFAULT_FRAGMENT_SHADER, PRESETS } from './constants';
import { ShaderError, UIState } from './types';

const App: React.FC = () => {
  const [code, setCode] = useState(DEFAULT_FRAGMENT_SHADER);
  const [error, setError] = useState<ShaderError | null>(null);
  const [uiState, setUiState] = useState<UIState>('both');
  const [showPresets, setShowPresets] = useState(false);

  // Sync hash with code if needed (simple share feature)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      try {
        const decoded = atob(hash.slice(1));
        if (decoded && decoded.trim().length > 0) {
          setCode(decoded);
        }
      } catch (e) {
        console.warn("Invalid hash code detected, ignoring.");
      }
    }
  }, []);

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
    // Persist to hash silently
    try {
      window.history.replaceState(null, '', '#' + btoa(newCode));
    } catch (e) {
      // In case of non-latin characters or other btoa limitations
    }
  };

  const toggleUI = () => {
    if (uiState === 'both') setUiState('preview');
    else if (uiState === 'preview') setUiState('editor');
    else setUiState('both');
  };

  return (
    <div className="relative w-screen h-screen bg-black text-white overflow-hidden">
      {/* Background Visualizer */}
      <Visualizer code={code} onError={setError} />

      {/* Overlay UI */}
      <div className="relative z-10 w-full h-full flex flex-col pointer-events-none p-4 md:p-8">
        
        {/* Header */}
        <header className="flex items-center justify-between mb-6 pointer-events-auto">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/></svg>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-white/90 leading-tight">Shader Studio</h1>
              <p className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider">By YSOH</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowPresets(!showPresets)}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-all text-sm font-medium backdrop-blur-md border border-white/10"
            >
              Presets
            </button>
            <button 
              onClick={toggleUI}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-all text-sm font-medium backdrop-blur-md border border-white/10"
            >
              {uiState === 'both' ? 'Hide UI' : uiState === 'preview' ? 'Show UI' : 'Split View'}
            </button>
          </div>
        </header>

        {/* Presets Popover */}
        {showPresets && (
          <div className="absolute top-24 right-8 w-64 bg-zinc-900/90 backdrop-blur-2xl border border-white/10 rounded-xl p-4 shadow-2xl pointer-events-auto animate-in fade-in zoom-in-95">
            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3">Gallery</h3>
            <div className="flex flex-col gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    handleCodeChange(preset.code);
                    setShowPresets(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm transition-colors text-zinc-300 hover:text-white"
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Workspace */}
        <main className={`flex-1 flex gap-6 transition-all duration-500 overflow-hidden ${uiState === 'preview' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
          
          {/* Editor Column */}
          <div className={`flex flex-col gap-4 pointer-events-auto transition-all duration-500 ${uiState === 'both' ? 'w-1/2' : 'w-full max-w-4xl mx-auto'}`}>
            <Editor value={code} onChange={handleCodeChange} error={error} />
          </div>

          {/* Stats/Debug Column (Only in Split View) */}
          {uiState === 'both' && (
            <div className="w-1/2 flex flex-col gap-4 pointer-events-none opacity-50 hover:opacity-100 transition-opacity">
               <div className="bg-zinc-950/20 backdrop-blur-md border border-white/5 rounded-2xl p-6 h-full flex flex-col justify-end">
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Performance</span>
                      <div className="text-2xl font-light text-zinc-300">60.0 <span className="text-sm opacity-50">FPS</span></div>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Resolution</span>
                      <div className="text-xl font-light text-zinc-300">Full Window <span className="text-xs opacity-50">(Auto)</span></div>
                    </div>
                    <div className="pt-4 border-t border-white/5 text-[10px] text-zinc-600 font-mono">
                      u_time: float<br/>
                      u_resolution: vec2<br/>
                      u_mouse: vec2
                    </div>
                  </div>
               </div>
            </div>
          )}
        </main>

        {/* Footer info */}
        <footer className={`mt-6 text-zinc-500 text-[10px] flex items-center justify-between transition-all duration-500 ${uiState === 'preview' ? 'opacity-0 translate-y-4' : 'opacity-100'}`}>
          <div className="flex items-center gap-4">
             <span>v1.1.0</span>
             <span>WebGL 2.0 (GLSL ES 3.00)</span>
          </div>
          <div className="font-mono uppercase">
             Precision Mediump Float
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;